import React, { useEffect } from 'react';
import { ALERT_TYPES } from './constants';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon,
  XMarkIcon 
} from '@heroicons/react/20/solid';
import classNames from 'classnames';

const STYLES = {
  [ALERT_TYPES.SUCCESS]: {
    container: 'bg-green-500/10 text-green-400 ring-green-500/20',
    icon: CheckCircleIcon,
    iconColor: 'text-green-400',
    buttonHover: 'hover:bg-green-500/20',
    focusRing: 'focus:ring-green-500',
  },
  [ALERT_TYPES.ERROR]: {
    container: 'bg-red-500/10 text-red-400 ring-red-500/20',
    icon: XCircleIcon,
    iconColor: 'text-red-400',
    buttonHover: 'hover:bg-red-500/20',
    focusRing: 'focus:ring-red-500',
  },
  [ALERT_TYPES.WARNING]: {
    container: 'bg-yellow-500/10 text-yellow-400 ring-yellow-500/20',
    icon: ExclamationTriangleIcon,
    iconColor: 'text-yellow-400',
    buttonHover: 'hover:bg-yellow-500/20',
    focusRing: 'focus:ring-yellow-500',
  },
  [ALERT_TYPES.INFO]: {
    container: 'bg-blue-500/10 text-blue-400 ring-blue-500/20',
    icon: InformationCircleIcon,
    iconColor: 'text-blue-400',
    buttonHover: 'hover:bg-blue-500/20',
    focusRing: 'focus:ring-blue-500',
  },
};

export default function Alert({
  type = ALERT_TYPES.INFO,
  title,
  children,
  open = true,
  onClose,
  dismissible = false,
  autoHideDuration = 0,
  className = '',
}) {
  useEffect(() => {
    if (open && autoHideDuration > 0 && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [open, autoHideDuration, onClose]);

  if (!open) return null;

  const style = STYLES[type] || STYLES[ALERT_TYPES.INFO];
  const Icon = style.icon;

  return (
    <div className={classNames('rounded-md p-4 ring-1 ring-inset', style.container, className)}>
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={classNames('h-5 w-5', style.iconColor)} aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1 md:flex md:justify-between">
          <div>
            {title && <h3 className={classNames('text-sm font-medium', style.iconColor)}>{title}</h3>}
            {children && <div className={classNames('text-sm opacity-90', title && 'mt-2')}>{children}</div>}
          </div>
        </div>
        {dismissible && onClose && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onClose}
                className={classNames(
                  'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2',
                  style.buttonHover,
                  style.iconColor,
                  style.focusRing,
                  'focus:ring-offset-gray-900'
                )}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
